import React, {
  Suspense,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  type ErrorLike,
  NetworkStatus,
  type OperationVariables,
} from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Card } from '@mui/material';
import { Route, Routes } from 'react-router-dom';

import type {
  ComparableCmaListingsQuery,
  ComparableCmaListingsQueryVariables,
} from '@graphql/__generated__/graphql';
import type { QuickFilterDefinition } from '@modules/_shared/components/filters/Filters';
import { SchemaInitializer } from '@modules/_shared/components/filters/SchemaProvider';
import { ListToolbar } from '@modules/_shared/components/list-toolbar/ListToolbar';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import RaGrid from '@modules/_shared/components/RaGrid';
import { SmartListTitle } from '@modules/_shared/components/SmartListTitle';
import { usePropertyTypeOptionRenderer } from '@modules/_shared/hooks/usePropertyTypeOptionRenderer';
import { PropertyTypesProvider } from '@modules/_shared/providers/PropertyTypesProvider';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { prepareWhereClauseQuery } from '@modules/_shared/utils/parseWhereClause';
import ComparableListingsSkeleton from '@modules/comparable-listings/ComparableListingsSkeleton';
import UrlSearch from '@modules/comparable-listings/UrlSearch';
import {
  COMPARABLE_CMA_LISTINGS,
  COMPARABLE_CMA_LISTINGS_COUNT,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import CmaScraperCard from '@modules/reports/cma-reports/step-listing-compare/CmaScraperCard';
import ListingComparableDetails from '@modules/reports/cma-reports/step-listing-compare/ListingComparableDetails';
import { SelectableCard } from '@modules/reports/cma-reports/step-listing-compare/SelectableCard';
import type { CmaListingWithDistance } from '@modules/reports/cma-reports/step-listing-compare/types';
import { useLocale } from '@shared/hooks/locale';
import type { SetHook } from '@shared/hooks/useSet';

export const COMPARABLE_GRID_PAGE_SIZE = 24;

type ComparableListingsQueryOptions = {
  query: typeof COMPARABLE_CMA_LISTINGS;
  variables: ComparableCmaListingsQueryVariables & OperationVariables;
  context?: { clientName: 'scrapers' };
};

type ComparableListingsWrapperProps = {
  children: React.ReactNode;
  variant: 'horizontal' | 'vertical';
  gridItemProps?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
};

const ComparableListingsFallback = React.memo(
  ({ variant, gridItemProps }: ComparableListingsWrapperProps) => (
    <RaGrid
      loading
      gridItemProps={gridItemProps}
      renderSkeleton={() => <ComparableListingsSkeleton variant={variant} />}
      renderItemCard={() => null}
    />
  ),
);

const ComparableListingsWrapper = React.memo(
  ({ children, variant, gridItemProps }: ComparableListingsWrapperProps) => (
    <Suspense
      fallback={
        <ComparableListingsFallback
          variant={variant}
          gridItemProps={gridItemProps}
        >
          {children}
        </ComparableListingsFallback>
      }
    >
      <SchemaInitializer isScraperSchema />
      <PropertyTypesProvider>{children}</PropertyTypesProvider>
    </Suspense>
  ),
);

export const PINNED_LISTING_ID_PARAM = 'pinned_listing_id';

const readPinnedListingIds = (
  searchParams: URLSearchParams,
): number[] | null => {
  const raw = searchParams.get(PINNED_LISTING_ID_PARAM);
  if (!raw) {
    return null;
  }
  const ids = raw
    .split(',')
    .map(part => Number(part.trim()))
    // Integers only (floats break the SQL `::int` cast); keep 0 (the sentinel).
    .filter(n => Number.isInteger(n) && n >= 0);
  return ids.length > 0 ? ids : null;
};

export const initialComparableListings = (
  searchParams: URLSearchParams,
  rowsPerPage: number = COMPARABLE_GRID_PAGE_SIZE,
  countryCode: string,
  lat?: number | null,
  lng?: number | null,
  stateId?: string | null,
): ComparableListingsQueryOptions => {
  const whereClause = prepareWhereClauseQuery(
    JSON.parse(searchParams.get('where') || '{}'),
  );

  const { offset } = getPaginationVariables(searchParams, undefined, true);
  const listingIds = readPinnedListingIds(searchParams);

  // Pinned ids must search all partitions; stateId would exclude cross-partition matches.
  const effectiveStateId = listingIds == null ? stateId : null;
  // Quick filters (locality, property type…) would exclude listings pinned from other regions.
  const effectiveWhere = listingIds == null ? whereClause : undefined;

  return {
    query: COMPARABLE_CMA_LISTINGS,
    variables: {
      pageSize: rowsPerPage,
      offset,
      lat: lat ?? 0,
      lng: lng ?? 0,
      stateId: effectiveStateId,
      listingIds,
      chWhere: effectiveWhere,
      esWhere: effectiveWhere,
      frWhere: effectiveWhere,
      itWhere: effectiveWhere,
      plWhere: effectiveWhere,
      ptWhere: effectiveWhere,
      isCh: countryCode === 'CH',
      isEs: countryCode === 'ES',
      isFr: countryCode === 'FR',
      isIt: countryCode === 'IT',
      isPl: countryCode === 'PL',
      isPt: countryCode === 'PT',
    },
    context: { clientName: 'scrapers' },
  };
};

type ComparableListingsProps = {
  selection: SetHook<string | number>;
  onSelect: (id: string | number) => void;
  lat?: number | null;
  lng?: number | null;
  stateId?: string | null;
  isDrawer?: boolean;
  queryParamsScope?: string;
  showAgencyName?: boolean;
  showContactName?: boolean;
  showDaysOnMarket?: boolean;
  showOfflineSince?: boolean;
  positionDateBadge?: 'image' | 'inline';
  variant?: 'horizontal' | 'vertical';
  gridItemProps?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
};

export type ComparableListingsCmaListing =
  | ComparableCmaListingsQuery['ch_get_cma_listings_distance']
  | ComparableCmaListingsQuery['es_get_cma_listings_distance']
  | ComparableCmaListingsQuery['fr_get_cma_listings_distance']
  | ComparableCmaListingsQuery['it_get_cma_listings_distance']
  | ComparableCmaListingsQuery['pl_get_cma_listings_distance']
  | ComparableCmaListingsQuery['pt_get_cma_listings_distance'];

type ListingCardProps = {
  cmaListingDistance: CmaListingWithDistance;
} & ComparableListingsProps;

const cardStyles = {
  cursor: 'pointer',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  flexGrow: 1,
};

export const ListingCard = ({
  cmaListingDistance,
  selection,
  onSelect,
  showAgencyName,
  showContactName,
  showDaysOnMarket,
  showOfflineSince,
  positionDateBadge,
  variant = 'vertical',
}: ListingCardProps) => {
  if (!selection) {
    return (
      <Card sx={cardStyles}>
        <CmaScraperCard
          cmaListingDistance={cmaListingDistance}
          variant={variant}
          showAgencyName={showAgencyName}
          showContactName={showContactName}
          showDaysOnMarket={showDaysOnMarket}
          showOfflineSince={showOfflineSince}
          positionDateBadge={positionDateBadge}
        />
      </Card>
    );
  }

  return (
    <SelectableCard
      selected={selection.has(cmaListingDistance.listing?.id ?? 0)}
      onChange={() => onSelect(cmaListingDistance.listing?.id ?? 0)}
      variant={variant}
      sx={cardStyles}
    >
      <CmaScraperCard
        cmaListingDistance={cmaListingDistance}
        variant={variant}
        showAgencyName={showAgencyName}
        showContactName={showContactName}
        showDaysOnMarket={showDaysOnMarket}
        showOfflineSince={showOfflineSince}
        positionDateBadge={positionDateBadge}
      />
    </SelectableCard>
  );
};

const ComparableListings = ({
  selection,
  onSelect,
  lat,
  lng,
  stateId,
  isDrawer = false,
  queryParamsScope,
  showAgencyName = false,
  showContactName = false,
  showDaysOnMarket = true,
  showOfflineSince = true,
  positionDateBadge = 'image',
  variant = 'vertical',
  gridItemProps,
}: ComparableListingsProps) => {
  const { t, countryCode } = useLocale();
  const [searchParams] = useScopedSearchParams(queryParamsScope);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [urlSearchEmptyMessage, setUrlSearchEmptyMessage] = useState<
    string | null
  >(null);
  const [urlSearchLoading, setUrlSearchLoading] = useState(false);

  const queryOptions = useMemo(
    () =>
      initialComparableListings(
        searchParams,
        COMPARABLE_GRID_PAGE_SIZE,
        countryCode,
        lat,
        lng,
        stateId,
      ),
    [searchParams, countryCode, lat, lng, stateId],
  );

  const {
    data: comparablesData,
    loading: comparablesLoading,
    refetch,
    networkStatus,
    error: comparablesError,
  } = useQuery(queryOptions.query, {
    variables: queryOptions.variables,
    context: { clientName: 'scrapers' },
    notifyOnNetworkStatusChange: true,
  });

  const countVariables = {
    lat: lat ?? 0,
    lng: lng ?? 0,
    stateId: queryOptions.variables?.stateId,
    listingIds: queryOptions.variables?.listingIds,
    isCh: countryCode === 'CH',
    isEs: countryCode === 'ES',
    isFr: countryCode === 'FR',
    isIt: countryCode === 'IT',
    isPl: countryCode === 'PL',
    isPt: countryCode === 'PT',
    chWhere: queryOptions.variables?.chWhere,
    esWhere: queryOptions.variables?.esWhere,
    frWhere: queryOptions.variables?.frWhere,
    itWhere: queryOptions.variables?.itWhere,
    plWhere: queryOptions.variables?.plWhere,
    ptWhere: queryOptions.variables?.ptWhere,
  };

  const {
    data: totalCountData,
    loading: totalCountLoading,
    error: totalCountError,
  } = useQuery(COMPARABLE_CMA_LISTINGS_COUNT, {
    variables: countVariables,
    context: { clientName: 'scrapers' },
  });

  useEffect(() => {
    const queryError = comparablesError ?? totalCountError;

    if (queryError) {
      setErrorLike(queryError);
    }
  }, [comparablesError, totalCountError]);

  const cmaListingsDistance = useMemo(() => {
    if (!comparablesData) {
      return [];
    }

    const rawListings =
      comparablesData.ch_get_cma_listings_distance ??
      comparablesData.es_get_cma_listings_distance ??
      comparablesData.fr_get_cma_listings_distance ??
      comparablesData.it_get_cma_listings_distance ??
      comparablesData.pl_get_cma_listings_distance ??
      comparablesData.pt_get_cma_listings_distance ??
      [];

    return rawListings.filter(
      (item): item is CmaListingWithDistance => item.listing?.id != null,
    );
  }, [comparablesData]);

  const totalCount = useMemo(() => {
    if (!totalCountData) {
      return 0;
    }

    return (
      totalCountData.ch_get_cma_listings_distance_aggregate?.aggregate?.count ??
      totalCountData.es_get_cma_listings_distance_aggregate?.aggregate?.count ??
      totalCountData.fr_get_cma_listings_distance_aggregate?.aggregate?.count ??
      totalCountData.it_get_cma_listings_distance_aggregate?.aggregate?.count ??
      totalCountData.pl_get_cma_listings_distance_aggregate?.aggregate?.count ??
      totalCountData.pt_get_cma_listings_distance_aggregate?.aggregate?.count ??
      0
    );
  }, [totalCountData]);

  const isLoading = useMemo(
    () => comparablesLoading || networkStatus === NetworkStatus.refetch,
    [comparablesLoading, networkStatus],
  );

  const tableName = `${countryCode.toLowerCase()}_cma_listings_distance`;

  const renderPropertyType = usePropertyTypeOptionRenderer();

  const quickFilters = useMemo<QuickFilterDefinition[]>(
    () => [
      {
        label: t('Location'),
        path: [`${tableName}_bool_exp`, 'listing', 'places'],
      },
      {
        label: t('Distance (meters)'),
        path: [`${tableName}_bool_exp`, 'distance_in_meters'],
      },
      {
        label: t('Online'),
        path: [`${tableName}_bool_exp`, 'listing', 'is_online'],
      },
      {
        label: t('Property type'),
        path: [`${tableName}_bool_exp`, 'listing', 'property_main_type', '_in'],
        filterAutocompleteRenderOption: renderPropertyType,
      },
      {
        label: t('Surfaces'),
        path: [`${tableName}_bool_exp`, 'listing', 'computed_surface'],
      },
      {
        label: t('Price'),
        path: [`${tableName}_bool_exp`, 'listing', 'sale_price'],
      },
      {
        label: t('Rooms'),
        path: [`${tableName}_bool_exp`, 'listing', 'number_of_rooms'],
      },
      {
        label: t('Price per m²'),
        path: [`${tableName}_bool_exp`, 'listing', 'computed_price_per_sqm'],
      },
      {
        label: t('Land surface'),
        path: [`${tableName}_bool_exp`, 'listing', 'land_surface'],
      },
      {
        label: t('Construction year'),
        path: [`${tableName}_bool_exp`, 'listing', 'construction_year'],
      },
      {
        label: t('Renovation year'),
        path: [`${tableName}_bool_exp`, 'listing', 'renovation_year'],
      },
    ],
    [renderPropertyType, t, tableName],
  );

  const MemoizedListingCard = useCallback(
    (item: CmaListingWithDistance) => (
      <ListingCard
        cmaListingDistance={item}
        selection={selection}
        onSelect={onSelect}
        showAgencyName={showAgencyName}
        showContactName={showContactName}
        showDaysOnMarket={showDaysOnMarket}
        showOfflineSince={showOfflineSince}
        positionDateBadge={positionDateBadge}
        variant={variant}
      />
    ),
    [
      selection,
      onSelect,
      showAgencyName,
      variant,
      showContactName,
      showDaysOnMarket,
      showOfflineSince,
      positionDateBadge,
    ],
  );

  return (
    <>
      <ComparableListingsWrapper
        variant={variant}
        gridItemProps={gridItemProps}
      >
        {!isDrawer && (
          <SmartListTitle
            tableName={tableName}
            defaultTitle={t('Comparable listings')}
          />
        )}
        <ListToolbar
          isScraperSchema
          tableName={tableName}
          refetch={refetch}
          quickFilters={quickFilters}
          queryParamsScope={queryParamsScope}
          searchComponent={
            <UrlSearch
              queryParamsScope={queryParamsScope}
              onEmptyMessageChange={setUrlSearchEmptyMessage}
              onLoadingChange={setUrlSearchLoading}
            />
          }
        />
        <RaGrid
          items={cmaListingsDistance}
          loading={isLoading || urlSearchLoading}
          loadingCount={totalCountLoading}
          totalCount={totalCount}
          emptyMessage={urlSearchEmptyMessage ?? undefined}
          pageSize={COMPARABLE_GRID_PAGE_SIZE}
          selection={selection}
          gridItemProps={gridItemProps}
          queryParamsScope={queryParamsScope}
          renderSkeleton={() => (
            <ComparableListingsSkeleton variant={variant} />
          )}
          renderItemCard={MemoizedListingCard}
        />
        {!isDrawer && (
          <Routes>
            <Route
              path=":encodedListingId"
              element={<ListingComparableDetails />}
            />
          </Routes>
        )}
      </ComparableListingsWrapper>

      <MutationErrorSnackbar
        onClose={() => setErrorLike(null)}
        error={apolloError}
      />
    </>
  );
};

export default memo(ComparableListings);
